import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import z from 'zod';

import type { CreateAsnDTO } from '@invenco/supply-interface';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import { Form } from '@/components/core/form';
import { TextareaFormField } from '@/components/forms/textarea-form-field';
import { OptionalWizardStep } from '@/components/wizard';
import type { WizardStepDefinition } from '@/components/wizard/wizard-context';
import { useHasFormValues } from '@/shared/hooks/use-has-form-values';
import { useIsFormValid } from '@/shared/hooks/use-is-form-valid';

const notesSchema = z.object({
  note: z.string().trim(),
});

const valueFields = ['note'] as const;

export function NotesStep({
  step,
  onChange,
}: {
  step: WizardStepDefinition;
  onChange: (input: Partial<CreateAsnDTO>) => void;
}) {
  const form = useForm({
    resolver: zodResolver(notesSchema),
    defaultValues: { note: '' },
  });
  const isValid = useIsFormValid(form, notesSchema);
  const hasValues = useHasFormValues(form, valueFields);

  const submitSection = async () => {
    let valid = false;

    await form.handleSubmit((data) => {
      onChange({ note: data.note || null });
      valid = true;
    })();

    return valid;
  };

  return (
    <OptionalWizardStep
      step={step}
      label="Add notes"
      complete={isValid && hasValues}
      onSubmitStep={submitSection}
    >
      <Card aria-label="Notes">
        <CardHeader>
          <CardTitle>Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <TextareaFormField
              control={form.control}
              name="note"
              aria-label="Notes"
              rows={4}
              placeholder="Add notes"
              autoFocus
            />
          </Form>
        </CardContent>
      </Card>
    </OptionalWizardStep>
  );
}
